(() => {
  const getVariantOptions = variant => {
    if (Array.isArray(variant.options) && variant.options.length) return variant.options;
    return [variant.option1, variant.option2, variant.option3].filter(value => value != null);
  };

  const getGalleryForProduct = productSection => {
    const handle = productSection.dataset.product;
    return [...document.querySelectorAll('[data-section-product-gallery]')]
      .find(gallery => gallery.dataset.product === handle);
  };

  const mediaIncludesVariant = (media, variantId) =>
    (media.dataset.dcVariantMediaIds || '')
      .split(',')
      .filter(Boolean)
      .includes(String(variantId));

  const getCurrentGalleryMedia = gallery =>
    [...gallery.querySelectorAll('[data-product-images] .product-media-item')]
      .filter(media => !media.classList.contains('slick-cloned'))
      .sort((a, b) => Number(a.dataset.dcMediaPosition) - Number(b.dataset.dcMediaPosition));

  const applyVariantMedia = (gallery, variantId) => {
    if (!gallery || !variantId) return;

    const galleryRoot = gallery.querySelector('[data-product-images]');
    if (!galleryRoot) return;

    const allMedia = getCurrentGalleryMedia(gallery);
    if (!allMedia.length) return;

    const matchingMedia = allMedia.filter(media => mediaIncludesVariant(media, variantId));
    const unassignedMedia = allMedia.filter(media => !(media.dataset.dcVariantMediaIds || '').trim());
    const visibleMedia = matchingMedia.length
      ? matchingMedia
      : (unassignedMedia.length ? unassignedMedia : allMedia.slice(0, 1));
    const visibleIds = new Set(visibleMedia.map(media => media.dataset.mediaId));

    // This native state is a resilient fallback when Slick has not initialized yet.
    allMedia.forEach(media => {
      const isVisible = visibleIds.has(media.dataset.mediaId);
      media.hidden = !isVisible;
      media.classList.toggle('product-single__media--selected', isVisible && media === visibleMedia[0]);
    });

    const thumbnailPanel = gallery.querySelector('#product-thumbnails');
    [...gallery.querySelectorAll('.product-thumbnail-wrap')].forEach(wrap => {
      const mediaId = wrap.querySelector('[data-media-id]')?.dataset.mediaId;
      const isVisible = visibleIds.has(mediaId);
      wrap.hidden = !isVisible;
      wrap.tabIndex = isVisible ? 0 : -1;
    });
    if (thumbnailPanel) {
      thumbnailPanel.hidden = visibleIds.size < 2;
      thumbnailPanel.dataset.dcVariantMediaCount = String(visibleIds.size);
    }

    galleryRoot.dataset.dcVariantMediaCount = String(visibleIds.size);
    galleryRoot.closest('.product-images')?.setAttribute('data-dc-variant-media-count', String(visibleIds.size));
    gallery.dataset.dcVariantMediaFor = String(variantId);

    const $gallery = window.jQuery && window.jQuery(galleryRoot);
    if (!$gallery || !$gallery.hasClass('slick-initialized')) return;

    try {
      $gallery.slick('slickUnfilter');
      // Slick supplies each candidate slide as `this` in the filter callback.
      $gallery.slick('slickFilter', function () {
        return visibleIds.has(this.dataset.mediaId);
      });
      $gallery.slick('slickGoTo', 0, true);
    } catch (_) {
      // The native hidden-state fallback above remains available while Slick reinitializes.
    }
  };

  const enhancePicker = picker => {
    if (picker.dataset.variantCardsReady === 'true') return;

    const form = picker.closest('form');
    const select = form?.querySelector('select[name="id"]');
    const productSection = picker.closest('[data-section-product-main]');
    const cards = [...picker.querySelectorAll('[data-variant-visual-option]')];
    if (!form || !select || !productSection || !cards.length) return;

    let product;
    try {
      product = JSON.parse(productSection.dataset.productJson || '{}');
    } catch (_) {
      return;
    }
    if (!Array.isArray(product.variants)) return;

    const gallery = getGalleryForProduct(productSection);
    const mediaTimers = [];
    const syncVariantMedia = variantId => {
      mediaTimers.forEach(timer => window.clearTimeout(timer));
      mediaTimers.length = 0;
      const selectedId = variantId || select.value;
      // Switch straight away, then repeat after the legacy gallery has completed lazy loading/reinitialization.
      applyVariantMedia(gallery, selectedId);
      [120, 420, 900].forEach(delay => {
        mediaTimers.push(window.setTimeout(() => applyVariantMedia(gallery, selectedId), delay));
      });
    };

    picker.dataset.variantCardsReady = 'true';
    document.documentElement.classList.add('dc-variant-enhanced');

    const sync = () => {
      const selectedId = String(select.value);
      cards.forEach(card => {
        const selected = card.dataset.variantId === selectedId;
        card.classList.toggle('is-selected', selected);
        card.setAttribute('aria-pressed', String(selected));
      });
    };

    const selectNativeOptions = variant => {
      const optionValues = getVariantOptions(variant);
      let lastChangedInput;

      optionValues.forEach((value, index) => {
        const inputs = [...form.querySelectorAll(`[data-product-swatch="${index}"] [data-product-swatch-input]`)];
        const input = inputs.find(item => item.value === String(value));
        if (!input) return;

        input.checked = true;
        input.closest('[data-product-swatch-value]')?.classList.add('option--active');
        input.closest('[data-product-swatch-value]')?.parentElement
          .querySelectorAll('[data-product-swatch-value]')
          .forEach(item => {
            if (item !== input.closest('[data-product-swatch-value]')) item.classList.remove('option--active');
          });
        lastChangedInput = input;
      });

      if (lastChangedInput) {
        lastChangedInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    };

    const selectVariant = card => {
      if (card.disabled) return;
      const variant = product.variants.find(item => String(item.id) === card.dataset.variantId && item.available !== false);
      if (!variant) return;

      select.value = String(variant.id);
      selectNativeOptions(variant);
      select.dispatchEvent(new Event('input', { bubbles: true }));
      select.dispatchEvent(new Event('change', { bubbles: true }));
      sync();
      syncVariantMedia(variant.id);
    };

    cards.forEach(card => card.addEventListener('click', () => selectVariant(card)));
    select.addEventListener('change', () => {
      sync();
      syncVariantMedia(select.value);
    });
    form.addEventListener('change', event => {
      if (event.target.matches('[data-product-swatch-input]')) syncVariantMedia(select.value);
    });

    sync();
    syncVariantMedia(select.value);
  };

  const initializeQuantityControls = () => {
    document.addEventListener('click', event => {
      const target = event.target instanceof Element ? event.target : null;
      const button = target?.closest('.qty-selector__btn');
      if (!button) return;

      const selector = button.closest('.qty-selector');
      const input = selector?.querySelector('[data-product-quantity]');
      if (!input) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const current = Number.parseInt(input.value, 10);
      const quantity = Number.isFinite(current) && current > 0 ? current : 1;
      const nextQuantity = button.classList.contains('qty-selector__btn--add')
        ? quantity + 1
        : Math.max(1, quantity - 1);

      input.value = String(nextQuantity);
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }, true);

    document.addEventListener('change', event => {
      const input = event.target instanceof HTMLInputElement && event.target.matches('[data-product-quantity]')
        ? event.target
        : null;
      if (!input) return;

      const value = Number.parseInt(input.value, 10);
      input.value = String(Number.isFinite(value) && value > 0 ? value : 1);
    });
  };

  const initialize = scope => (scope || document)
    .querySelectorAll('[data-variant-visual-picker]')
    .forEach(enhancePicker);

  initializeQuantityControls();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(document));
  } else {
    initialize(document);
  }
  document.addEventListener('shopify:section:load', event => initialize(event.target));
})();
